import ElakayImage from '../../../components/images/ElakayImage';
// Provider store configuration for the shared PageRenderer (see its `storeConfig` prop). The Page
// Builder document, templates and renderer are the Seller ones; only what differs in the business
// model is configured here:
//   - shop product elements render B2B product cards with Provider actions (View Product,
//     Buy Wholesale, Add to My Shop), never retail Buyer cards / Add to Cart;
//   - the shop header element renders the Provider identity with REAL statistics;
//   - retail-only elements (checkout, votes, retail price, Seller ad slider) are hidden;
//   - template sample statistics ("28", "100%", "4.8/5") never show; an unbound rating shows the
//     Provider's real rating or nothing;
//   - product paging/search stays on the Provider products already loaded (never the Seller shop API).
// `mode`: "public" (Sellers / professional roles) or "owner" (the Provider's own Customer View and
// Builder preview: identical rendering, but Seller-only actions are shown inert).
import React from "react";
import { Link } from "react-router-dom";
import { elakayAutoText as text, elakayPluralText } from "../../pageInside/dataElakay";
import ProviderProductCard from "../components/ProviderProductCard";
import { PROVIDER_ROUTES, providerCatalogPath } from "../providerWorkspace";
import { isTemplateSampleStat, isUnboundNode } from "./storefrontPages";
import "../ui/ui.css";
import "./providerStorefront.css";
import { getNameInitials } from "../utils/providerShopIdentity";

export const PROVIDER_PRODUCT_FILTERS = ["all", "wholesale", "dropshipping", "new"];
const LIST_BY_FILTER = { all: "providerProducts", wholesale: "wholesaleProducts", dropshipping: "dropshippingProducts", new: "newProducts" };

export const productsFor = (data, filter = "all") => {
  const key = LIST_BY_FILTER[PROVIDER_PRODUCT_FILTERS.includes(filter) ? filter : "all"];
  return Array.isArray(data?.[key]) ? data[key] : (Array.isArray(data?.products) ? data.products : []);
};

// `capabilities` (storefront-level, from the Provider Settings): a channel the Provider turned off is
// never offered, even on a product that still has it enabled. The server refuses it anyway.
export const StorefrontProductCard = ({ product, mode = "public", capabilities = null }) => {
  const owner = mode === "owner";
  const canWholesale = Boolean(product.wholesaleEnabled) && capabilities?.wholesale !== false;
  const canDropship = Boolean(product.dropshippingEnabled) && capabilities?.dropshipping !== false;
  const inShop = Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  const inert = { "aria-disabled": true, tabIndex: -1, title: text("h_psf_seller_action", "Available to Sellers on your storefront"), onClick: (e) => e.preventDefault() };
  const action = (to, label, extraClass = "") => (owner
    ? <span className={`psf-card__action psf-card__action--inert ${extraClass}`} {...inert}>{label}</span>
    : <Link className={`psf-card__action ${extraClass}`} to={to}>{label}</Link>);
  return (
    <div className="psf-card" data-product-id={product._id}>
      <ProviderProductCard variant="browse" product={product} showProvider={false} to={owner ? PROVIDER_ROUTES.editProduct(product._id) : undefined} />
      <div className="psf-card__actions">
        {action(`/seller/provider-products/${product._id}`, text("h_psf_view", "View Product"))}
        {canWholesale && action(`/seller/provider-products/${product._id}/buy`, text("h_psf_buy_wholesale", "Buy Wholesale"), "psf-card__action--primary")}
        {canDropship && (inShop
          ? action("/seller/provider-products", text("h_ppc_inshop", "In My Shop"))
          : action(`/seller/provider-products/add/${product._id}`, text("h_psf_add_shop", "Add to My Shop")))}
      </div>
    </div>
  );
};

export const StorefrontProductList = ({ products, mode, limit, empty, capabilities = null }) => {
  const list = Number.isFinite(limit) && limit > 0 ? products.slice(0, limit) : products;
  if (!list.length) return <p className="psf-empty">{empty || text("h_psf_no_products", "No products available yet.")}</p>;
  return <div className="pgrid psf-grid">{list.map((p) => <StorefrontProductCard key={p._id} product={p} mode={mode} capabilities={capabilities} />)}</div>;
};

const FILTER_TITLES = {
  all: ["h_psf_products", "Products"],
  wholesale: ["h_psf_wholesale_products", "Wholesale Opportunities"],
  dropshipping: ["h_psf_dropshipping_products", "Available for Dropshipping"],
  new: ["h_psf_new_products", "New Products"],
};

const makeProductsBlock = (mode, single = false) => function ProviderProductsBlock({ data, options }) {
  const filter = options.providerFilter || "all";
  const products = productsFor(data, filter);
  if (single) {
    const index = Number.isInteger(options.liveProductIndex) ? options.liveProductIndex : Number.isInteger(options.productIndex) ? options.productIndex : 0;
    const product = products[index];
    return product ? <StorefrontProductCard product={product} mode={mode} capabilities={data?.provider?.capabilities} /> : null;
  }
  const title = options.title ?? (options.providerFilter ? text(...FILTER_TITLES[PROVIDER_PRODUCT_FILTERS.includes(filter) ? filter : "all"]) : "");
  return (
    <section className="psf-block">
      {title && <h2 className="psf-block__title">{title}</h2>}
      <StorefrontProductList products={products} mode={mode} limit={Number(options.limit) || 12} capabilities={data?.provider?.capabilities} />
    </section>
  );
};

const statEntries = (stats = {}) => [
  Number.isFinite(stats.activeProducts) && { key: "products", value: stats.activeProducts, label: text("h_psf_stat_products", "Products") },
  Number.isFinite(stats.wholesaleProducts) && stats.wholesaleProducts > 0 && { key: "wholesale", value: stats.wholesaleProducts, label: text("h_psf_stat_wholesale", "Wholesale") },
  Number.isFinite(stats.dropshippingProducts) && stats.dropshippingProducts > 0 && { key: "dropshipping", value: stats.dropshippingProducts, label: text("h_psf_stat_dropshipping", "Dropshipping") },
  Number.isFinite(stats.connectedSellers) && stats.connectedSellers > 0 && { key: "sellers", value: stats.connectedSellers, label: text("h_psf_stat_sellers", "Connected Sellers") },
  Number.isFinite(stats.rating) && { key: "rating", value: `★ ${stats.rating}`, label: elakayPluralText(stats.ratingCount, ["h_psf_stat_rating_one", "{value0} rating"], ["h_psf_stat_rating", "{value0} ratings"], { value0: stats.ratingCount }) },
  Number.isFinite(stats.likes) && stats.likes > 0 && { key: "likes", value: stats.likes, label: text("h_psf_stat_likes", "Likes") },
].filter(Boolean);

export const ProviderStats = ({ stats }) => {
  const entries = statEntries(stats);
  if (!entries.length) return null;
  return <ul className="psf-stats">{entries.map((e) => <li key={e.key} data-stat={e.key}><strong>{e.value}</strong><span>{e.label}</span></li>)}</ul>;
};

const ProviderIdentityBlock = ({ data, options }) => {
  const provider = data?.provider || {};
  const section = options.providerSection || "header";
  if (section === "stats") return <ProviderStats stats={provider.stats} />;
  const logo = provider.avatar
    ? <ElakayImage preset="avatar" className="psf-identity__logo" src={provider.avatar} alt="" />
    : <span className="psf-identity__logo psf-identity__logo--ph" aria-hidden="true">{provider.monogram || getNameInitials(provider.shopName).slice(0, 3) || "P"}</span>;
  if (section === "info") {
    const services = [
      provider.stats?.wholesaleProducts > 0 && text("h_psf_service_wholesale", "Wholesale purchasing"),
      provider.stats?.dropshippingProducts > 0 && text("h_psf_service_dropshipping", "Dropshipping for your shop"),
    ].filter(Boolean);
    return (
      <section className="psf-info">
        {logo}
        <div>
          <h2>{provider.shopName}</h2>
          {provider.location && <p className="psf-info__location">{provider.location}</p>}
          {services.length > 0 && <ul className="psf-info__services">{services.map((s) => <li key={s}>{s}</li>)}</ul>}
          <ProviderStats stats={provider.stats} />
        </div>
      </section>
    );
  }
  return (
    <section className="psf-identity">
      {logo}
      <div className="psf-identity__text">
        <h1>{provider.shopName}</h1>
        {provider.location && <p>{provider.location}</p>}
      </div>
      <ProviderStats stats={provider.stats} />
    </section>
  );
};

const makeCategoriesBlock = (mode) => function ProviderCategoriesBlock({ data, options }) {
  const categories = data?.categories?.categories || [];
  if (!categories.length) return null;
  const providerId = data?.provider?._id;
  const label = (c) => (Array.isArray(c.name) ? c.name[0] : String(c.name || ""));
  return (
    <section className="psf-block">
      {options.title !== "" && <h2 className="psf-block__title">{options.title || text("h_psf_categories", "Categories")}</h2>}
      <div className="psf-chips">
        {categories.map((c) => (mode === "owner"
          ? <span key={c._id} className="psf-chip">{label(c)}{Number.isFinite(c.products) ? ` (${c.products})` : ""}</span>
          : <Link key={c._id} className="psf-chip" to={providerCatalogPath(providerId, c._id)}>{label(c)}{Number.isFinite(c.products) ? ` (${c.products})` : ""}</Link>))}
      </div>
    </section>
  );
};

// An unbound rating element is a hard-coded claim: show the Provider's real rating or nothing.
// Provider-conditional elements (the generated About page uses them, any page may):
//   options.providerRequires   "wholesale" | "dropshipping" -> hidden when that channel is not offered
//   options.providerWhenRating "none" | "some"               -> shown only without / with a real rating
export const transformNode = (node, data) => {
  if (isTemplateSampleStat(node)) return null;
  const provider = data?.provider || {};
  const requires = node?.options?.providerRequires;
  if (requires && provider.capabilities && provider.capabilities[requires] !== true) return null;
  const whenRating = node?.options?.providerWhenRating;
  if (whenRating && (whenRating === "some") !== Number.isFinite(provider.stats?.rating)) return null;
  if (node?.type === "rating" && isUnboundNode(node)) {
    const rating = data?.provider?.stats?.rating;
    return Number.isFinite(rating) ? { ...node, content: { ...(node.content || {}), value: rating } } : null;
  }
  return node;
};

// `navigate`: react-router navigate (no full reload). Product actions always lead to the professional
// product page (public) or the Provider's own editor (owner) -- never a retail cart.
export const buildProviderStoreConfig = ({ mode = "public", navigate = null } = {}) => {
  const go = (path) => { if (path && navigate) navigate(path); return path; };
  const idOf = (p) => (p && typeof p === "object" ? p._id || p.id : p);
  const productPath = (p) => (mode === "owner" ? PROVIDER_ROUTES.editProduct(idOf(p)) : `/seller/provider-products/${idOf(p)}`);
  const ProductsBlock = makeProductsBlock(mode);
  return {
    storeType: "provider",
    mode,
    localProducts: true,
    disableFollowBar: true,
    hidePageNavigation: true,
    transformNode,
    components: {
      shopProductGrid: ProductsBlock,
      shopCarousel: ProductsBlock,
      shopPubMatrix: ProductsBlock,
      shopCard: makeProductsBlock(mode, true),
      shopHeaderSeller: ProviderIdentityBlock,
      shopCatMatrix: makeCategoriesBlock(mode),
      shopCheckout: null,
      shopVote: null,
      shopPrice: null,
      shopSlider: null,
      showImage: null,
    },
    actions: {
      viewProduct: (p) => (idOf(p) ? go(productPath(p)) : null),
      addToCart: async (p) => { if (idOf(p)) go(productPath(p)); return { redirected: true }; },
      buyNow: async (p) => { if (idOf(p)) go(productPath(p)); return { redirected: true }; },
      goToCart: () => null,
      goToCheckout: () => null,
      getCart: async () => ({ items: [] }),
      clickToFollowShop: async () => ({ ignored: true }),
    },
  };
};

// Builder "Provider" element group: the same shop elements, pre-configured for Provider data.
export const PROVIDER_ELEMENT_PRESETS = [
  {
    key: "providerStore",
    get label() { return text("h_psf_preset_group", "🏭 Provider"); },
    items: [
      { key: "providerHeader", icon: "🏷", get label() { return text("h_psf_preset_header", "Provider Header"); }, make: () => ({ type: "shopHeaderSeller", style: { width: "100%" }, options: { providerSection: "header" }, bindSource: "store.provider" }) },
      { key: "providerInfo", icon: "ℹ️", get label() { return text("h_psf_preset_info", "Provider Information"); }, make: () => ({ type: "shopHeaderSeller", style: { width: "100%" }, options: { providerSection: "info" }, bindSource: "store.provider" }) },
      { key: "providerStats", icon: "📊", get label() { return text("h_psf_preset_stats", "Provider Stats"); }, make: () => ({ type: "shopHeaderSeller", style: { width: "100%" }, options: { providerSection: "stats" }, bindSource: "store.provider" }) },
      { key: "providerProducts", icon: "📦", get label() { return text("h_psf_preset_products", "Provider Products"); }, make: () => ({ type: "shopProductGrid", style: { width: "100%" }, options: { providerFilter: "all", limit: 12 }, bindSource: "store.providerProducts" }) },
      { key: "wholesaleProducts", icon: "🏬", get label() { return text("h_psf_preset_wholesale", "Wholesale Products"); }, make: () => ({ type: "shopProductGrid", style: { width: "100%" }, options: { providerFilter: "wholesale", limit: 8 }, bindSource: "store.wholesaleProducts" }) },
      { key: "dropshippingProducts", icon: "🚚", get label() { return text("h_psf_preset_dropshipping", "Dropshipping Products"); }, make: () => ({ type: "shopProductGrid", style: { width: "100%" }, options: { providerFilter: "dropshipping", limit: 8 }, bindSource: "store.dropshippingProducts" }) },
      { key: "newProducts", icon: "✨", get label() { return text("h_psf_preset_new", "New Products"); }, make: () => ({ type: "shopProductGrid", style: { width: "100%" }, options: { providerFilter: "new", limit: 8 }, bindSource: "store.newProducts" }) },
      { key: "providerCategories", icon: "🗂", get label() { return text("h_psf_preset_categories", "Categories"); }, make: () => ({ type: "shopCatMatrix", style: { width: "100%" }, options: {}, bindSource: "store.categories" }) },
    ],
  },
];

